import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material'
import {
  Timeline as TimelineIcon,
  Pool as PoolIcon,
  WaterDrop as WaterIcon,
  Science as ScienceIcon,
  Thermostat as TempIcon,
  ShowChart as ShowChartIcon
} from '@mui/icons-material'
import Layout from '../components/Layout/Layout'
import { usePool } from '../contexts/PoolContext'
import { useMeasurements } from '../hooks/useMeasurements'
import { useChemicalAdditions } from '../hooks/useChemicalAdditions'
import PoolSelector from '../components/Pool/PoolSelector'
import StatusBadge from '../components/Common/StatusBadge'
import HistoryChart from '../components/Charts/HistoryChart'
import TimeRangeSelector from '../components/Charts/TimeRangeSelector'
import usePageTracking from '../hooks/usePageTracking'
import {
  prepareChartData,
  getChemicalAdditionsInRange,
  calculateDateRange
} from '../utils/chartUtils'

const History = () => {
  usePageTracking('History');
  
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { pools, selectedPool, setSelectedPool } = usePool()
  const { measurements, loading } = useMeasurements(selectedPool?.id)
  const { chemicalAdditions, loading: chemicalLoading } = useChemicalAdditions(selectedPool?.id)
  
  // Chart state
  const [selectedRange, setSelectedRange] = useState('1M')
  const [customStartDate, setCustomStartDate] = useState(null)
  const [customEndDate, setCustomEndDate] = useState(null)
  const [showChemicals, setShowChemicals] = useState(true)
  
  // Calculate date range for filtering
  const { startDate, endDate } = useMemo(() => 
    calculateDateRange(selectedRange, customStartDate, customEndDate)
  , [selectedRange, customStartDate, customEndDate])
  
  // Prepare chart data
  const chartData = useMemo(() => 
    prepareChartData(measurements, startDate, endDate)
  , [measurements, startDate, endDate])
  
  // Filter chemical additions for chart
  const filteredChemicalAdditions = useMemo(() => 
    getChemicalAdditionsInRange(chemicalAdditions, startDate, endDate)
  , [chemicalAdditions, startDate, endDate])

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-'
    return typeof value === 'number' ? value.toFixed(1) : value
  }

  const handlePoolChange = (pool) => {
    setSelectedPool(pool)
  }

  const parameters = [
    { key: 'free_chlorine', label: 'FC', fullLabel: 'Free Chlorine', unit: 'ppm', color: 'success', icon: <WaterIcon /> },
    { key: 'combined_chlorine', label: 'CC', fullLabel: 'Combined Chlorine', unit: 'ppm', color: 'warning', icon: <WaterIcon /> },
    { key: 'ph', label: 'pH', fullLabel: 'pH', unit: '', color: 'info', icon: <ScienceIcon /> },
    { key: 'total_alkalinity', label: 'TA', fullLabel: 'Total Alkalinity', unit: 'ppm', color: 'info', icon: <ScienceIcon /> },
    { key: 'cya_stabilizer', label: 'CYA', fullLabel: 'CYA/Stabilizer', unit: 'ppm', color: 'warning', icon: <ScienceIcon /> },
    { key: 'calcium_hardness', label: 'CH', fullLabel: 'Calcium Hardness', unit: 'ppm', color: 'info', icon: <ScienceIcon /> },
    { key: 'water_temperature', label: 'Temp', fullLabel: 'Water Temperature', unit: '°F', color: 'info', icon: <TempIcon /> },
  ]

  // Show pool selection if no pool is selected
  if (!selectedPool && pools.length > 0) {
    return (
      <Layout>
        <Box sx={{ maxWidth: 'lg', mx: 'auto', p: 2 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              textAlign: 'center'
            }}
          >
            <Stack spacing={3} alignItems="center">
              <TimelineIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                Select a Pool
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Choose a pool to view its measurement history
              </Typography>
              <Box sx={{ minWidth: 300 }}>
                <PoolSelector
                  selectedPool={selectedPool}
                  onPoolChange={handlePoolChange}
                  label="Pool"
                  showAlert={false}
                />
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Layout>
    )
  }

  // Show no pools state
  if (pools.length === 0) {
    return (
      <Layout>
        <Box sx={{ maxWidth: 'lg', mx: 'auto', p: 2 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              textAlign: 'center'
            }}
          >
            <Stack spacing={3} alignItems="center">
              <PoolIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                No Pools Found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create a pool first to view its measurement history.
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Layout>
    )
  }

  if (loading) {
    return (
      <Layout>
        <Box sx={{ maxWidth: 'lg', mx: 'auto', p: 2 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              textAlign: 'center'
            }}
          >
            <Stack spacing={3} alignItems="center">
              <CircularProgress size={48} />
              <Typography variant="h6" color="text.secondary">
                Loading measurement history...
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Layout>
    )
  }

  return (
    <Layout>
      <Box sx={{ maxWidth: 'lg', mx: 'auto', p: 2 }}>
        <Stack spacing={3}>
          {/* Header Section */}
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            }}
          >
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TimelineIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Box>
                  <Typography 
                    variant={isMobile ? 'h5' : 'h4'} 
                    component="h1" 
                    sx={{ 
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Measurement History
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {selectedPool ? `Historical data for ${selectedPool.name}` : 'Select a pool to view history'}
                  </Typography>
                </Box>
              </Box>
              
              {/* Pool Selector */}
              <Box sx={{ maxWidth: 400 }}>
                <PoolSelector
                  selectedPool={selectedPool}
                  onPoolChange={handlePoolChange}
                  label="Pool"
                  showAlert={false}
                />
              </Box>
            </Stack>
          </Paper>

          {/* Content Section */}
          {measurements.length === 0 ? (
            <Paper 
              elevation={2} 
              sx={{ 
                p: 4, 
                borderRadius: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
              }}
            >
              <Stack spacing={3} alignItems="center">
                <TimelineIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  No measurements yet
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Start tracking your pool chemistry to see trends over time.
                </Typography>
              </Stack>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {/* Chart Section */}
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <ShowChartIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                    Chemistry Trends
                  </Typography>
                </Box>
                
                {/* Time Range Selector */}
                <TimeRangeSelector
                  selectedRange={selectedRange}
                  onRangeChange={setSelectedRange}
                  customStartDate={customStartDate}
                  customEndDate={customEndDate}
                  onCustomDateChange={(start, end) => {
                    setCustomStartDate(start)
                    setCustomEndDate(end)
                  }}
                  disabled={loading || chemicalLoading}
                />
                
                {/* Chart Options */}
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showChemicals}
                        onChange={(e) => setShowChemicals(e.target.checked)}
                        disabled={loading || chemicalLoading}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Show Chemical Additions
                      </Typography>
                    }
                  />
                </Box>
                
                {/* Chart */}
                <HistoryChart
                  data={chartData}
                  chemicalAdditions={showChemicals ? filteredChemicalAdditions : []}
                  showChemicals={showChemicals}
                  poolConfig={selectedPool}
                  loading={loading || chemicalLoading}
                />
              </Paper>
              
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                  Raw Data
                </Typography>
              </Divider>
              
              {/* Data Table */}
              <Paper 
                elevation={2} 
                sx={{ 
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  overflow: 'hidden'
                }}
              >
                <TableContainer sx={{ maxHeight: isMobile ? 400 : 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600,
                            backgroundColor: 'primary.main',
                            color: 'white',
                            minWidth: 120
                          }}
                        >
                          Date
                        </TableCell>
                        {parameters.map(({ key, label, fullLabel, color, icon }) => (
                          <TableCell 
                            key={key}
                            align="center" 
                            sx={{ 
                              fontWeight: 600,
                              backgroundColor: `${color}.main`,
                              color: 'white',
                              minWidth: isMobile ? 60 : 80,
                              px: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {!isMobile && React.cloneElement(icon, { fontSize: 'small' })}
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {label}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {measurements.map((measurement, index) => (
                        <TableRow 
                          key={measurement.id}
                          sx={{ 
                            '&:hover': { backgroundColor: 'grey.50' },
                            backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.25'
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {formatDate(measurement.measured_at)}
                          </TableCell>
                          {parameters.map(({ key, unit, color }) => (
                            <TableCell key={key} align="center" sx={{ px: 1 }}>
                              <StatusBadge
                                label={`${formatValue(measurement[key])}${measurement[key] !== null && measurement[key] !== undefined && unit ? unit : ''}`}
                                color={measurement[key] !== null && measurement[key] !== undefined ? color : 'info'}
                                size="small"
                                sx={{
                                  minWidth: isMobile ? 45 : 55,
                                  fontSize: isMobile ? '0.65rem' : '0.75rem',
                                  opacity: measurement[key] !== null && measurement[key] !== undefined ? 1 : 0.5
                                }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          )}
        </Stack>
      </Box>
    </Layout>
  )
}

export default History